import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../../../core/constants/api-endpoints';

interface SendOtpRequest {
  email: string;
}

interface VerifyEmailRequest {
  email: string;
  code: string;
}

interface AuthResponse {
  message: string;
}

interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    accessToken: string;
    refreshToken: string;
    role: string | null;
    isSubscriptionActive: boolean;
    user: {
      id: string;
      firstName: string;
      lastName: string;
    };
  };
}

export interface UserProfile {
  id: string;
  createdAt: string;
  updatedAt: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  countryCode: string;
  phone: string;
  email: string;
  dob: string | null;
  verified: boolean;
  status: string;
  registrationStatus: string;
  emailVerifiedAt: string | null;
  addresses: unknown[];
  role: string;
  pharmacy: {
    id: string;
    name: string;
    description: string | null;
    licenseNumber: string | null;
    phone: string;
    email: string;
    status: string;
    isVerified: boolean;
    rbcId: string | null;
    ownerName: string;
    operatingName: string;
    managerFirstName: string;
    managerLastName: string;
    taxId: string | null;
    payoutFrequency: string;
    gstNumber: string | null;
    bankName: string | null;
    institutionNumber: string | null;
    transitNumber: string | null;
    accountNumber: string | null;
    address: unknown | null;
    documents: {
      voidChequeImage: string | null;
      gstProofImage: string | null;
      pharmacyLicenceImage: string | null;
      businessRegistrationImage: string | null;
    };
  } | null;
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  data: UserProfile;
}

export interface UploadImageResponse {
  success: boolean;
  message: string;
  data: {
    tempPath: string;
    originalName: string;
  };
}

export interface DriverRegisterRequest {
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  password: string;
  countryCode: string;
  phone: string;
  dob: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  formattedAddress: string;
  govtIdFront: string;
  govtIdBack: string;
  selfie: string;
}

export interface VendorRegisterRequest {
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  password: string;
  countryCode: string;
  phone: string;
  pharmacyName: string;
  operatingName: string;
  pharmacyPhone: string;
  pharmacyAddress: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    formattedAddress: string;
  };
  managerFirstName: string;
  managerLastName: string;
  payoutFrequency: string;
  gstNumber: string;
  voidChequeImage: string;
  gstProofImage?: string;
  pharmacyLicenceImage?: string;
  businessRegistrationImage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(API_ENDPOINTS.auth.login, {
      email,
      password,
    } satisfies LoginRequest);
  }

  sendOtp(email: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.sendOtp, {
      email,
    } satisfies SendOtpRequest);
  }

  verifyEmail(email: string, code: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.verifyEmail, {
      email,
      code,
    } satisfies VerifyEmailRequest);
  }

  registerDriver(data: DriverRegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.driverRegister, data);
  }

  registerVendor(data: VendorRegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.vendorRegister, data);
  }

  registerCustomer(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.customerRegister, {
      email,
      password,
    });
  }

  forgotPassword(email: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.forgotPassword, { email });
  }

  resetPassword(email: string, code: string, newPassword: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.resetPassword, { email, code, newPassword });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(API_ENDPOINTS.auth.changePassword, { currentPassword, newPassword });
  }

  refreshToken(refreshToken: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(API_ENDPOINTS.auth.refreshToken, { refreshToken });
  }

  getProfile(): Observable<ProfileResponse> {
    return this.http.get<ProfileResponse>(API_ENDPOINTS.auth.profile);
  }
}
